import { AccessKeyed, AccessMember, AccessScope, Assignment, Binary, CallFunction, CallMember, CallScope, Conditional, LiteralArray, LiteralObject, LiteralPrimitive, PrefixNot, StringInterpolation, } from './Ast';
import { ParseError } from './Error';
import { IdentifierToken, Lexer, OperatorToken, ValueToken } from './Lexer';
const debug = false;
export const parserDefaults = {
    rejectAssignment: true,
};
export class Parser {
    constructor(input, options = {}) {
        this.index = 0;
        this.tokens = [];
        this.options = Object.assign(Object.assign({}, parserDefaults), options);
        this.tokens = Lexer.lex(input);
        if (debug)
            console.log(`jsj: tokens = ${JSON.stringify(this.tokens, null, 4)}`);
    }
    advance() {
        this.index++;
    }
    advancePastOptionalOperator(value) {
        const token = this.getToken();
        if (token !== undefined && token instanceof OperatorToken && token.value === value) {
            this.advance();
            return true;
        }
        return false;
    }
    advancePastOptionalValue(value) {
        const token = this.getToken();
        if (token !== undefined && token instanceof ValueToken && token.value === value) {
            this.advance();
            return true;
        }
        return false;
    }
    advancePastRequiredOperator(value, message) {
        var _a, _b;
        const found = this.advancePastOptionalOperator(value);
        if (!found) {
            throw new ParseError((_b = (_a = this.getToken()) === null || _a === void 0 ? void 0 : _a.position) !== null && _b !== void 0 ? _b : 0, message !== null && message !== void 0 ? message : `expected ${value}`);
        }
    }
    getToken(relativePosition = 0) {
        return this.tokens[this.index + relativePosition];
    }
    getTokenRequired(relativePosition = 0, message) {
        var _a, _b;
        const token = this.getToken(relativePosition);
        if (token === undefined) {
            throw new ParseError((_b = (_a = this.getToken(-1)) === null || _a === void 0 ? void 0 : _a.position) !== null && _b !== void 0 ? _b : 0, message !== null && message !== void 0 ? message : `missing token`);
        }
        return token;
    }
    parse() {
        if (this.tokens.length === 0)
            return new LiteralPrimitive(0, undefined);
        return this.parseExpression();
    }
    parseAccessOrCallMember() {
        var _a, _b, _c;
        if (debug)
            console.log(`jsj: parseAccessOrCallMember: index = ${this.index}`);
        let result = this.parsePrimary();
        while (true) {
            if (this.advancePastOptionalOperator('.')) {
                const token = this.getToken();
                if (token === undefined ||
                    !(token instanceof IdentifierToken) ||
                    typeof token.value !== 'string') {
                    throw new ParseError((_c = (_a = token === null || token === void 0 ? void 0 : token.position) !== null && _a !== void 0 ? _a : (_b = this.getToken(-1)) === null || _b === void 0 ? void 0 : _b.position) !== null && _c !== void 0 ? _c : 0, 'invalid identifier');
                }
                this.advance();
                if (this.advancePastOptionalOperator('(')) {
                    const args = this.parseExpressionList(')');
                    result = new CallMember(token.position, result, token.value, args);
                }
                else {
                    if (!result.isAssignable) {
                        if (debug)
                            console.log(`jsj: result = ${JSON.stringify(result, null, 4)}`);
                        throw new ParseError(token.position, 'expression is not assignable');
                    }
                    result = new AccessMember(token.position, result, token.value);
                }
            }
            else if (this.advancePastOptionalOperator('[')) {
                const token = this.getTokenRequired(-1);
                const key = this.parseExpression();
                this.advancePastRequiredOperator(']');
                if (!result.isAssignable) {
                    throw new ParseError(token.position, 'expression is not assignable');
                }
                result = new AccessKeyed(token.position, result, key);
            }
            else if (this.advancePastOptionalOperator('(')) {
                const token = this.getTokenRequired(-1);
                const args = this.parseExpressionList(')');
                result = new CallFunction(token.position, result, args);
            }
            else {
                return result;
            }
        }
    }
    parseAccessOrCallScope() {
        if (debug)
            console.log(`jsj: parseAccessOrCallScope: index = ${this.index}`);
        const token = this.getTokenRequired();
        if (!(token instanceof IdentifierToken)) {
            throw new ParseError(token.position, `invalid identifier: "${token.value}"`);
        }
        this.advance();
        if (this.advancePastOptionalOperator('(')) {
            const args = this.parseExpressionList(')');
            return new CallScope(token.position, token.value, args);
        }
        return new AccessScope(token.position, token.value);
    }
    parseAdditive() {
        if (debug)
            console.log(`jsj: parseAdditive: index = ${this.index}`);
        const operators = ['+', '-'];
        let result = this.parseMultiplicative();
        while (true) {
            let found = false;
            for (const operator of operators) {
                if (this.advancePastOptionalOperator(operator)) {
                    result = new Binary(this.getTokenRequired(-1).position, operator, result, this.parseMultiplicative());
                    found = true;
                }
            }
            if (!found)
                return result;
        }
    }
    parseConditional() {
        var _a, _b;
        if (debug)
            console.log(`jsj: parseConditional: index = ${this.index}`);
        const start = (_b = (_a = this.getToken()) === null || _a === void 0 ? void 0 : _a.position) !== null && _b !== void 0 ? _b : 0;
        let result = this.parseLogicalOr();
        if (this.advancePastOptionalOperator('?')) {
            const yes = this.parseExpression();
            this.advancePastRequiredOperator(':');
            const no = this.parseExpression();
            result = new Conditional(start, result, yes, no);
        }
        return result;
    }
    parseEquality() {
        if (debug)
            console.log(`jsj: parseEquality: index = ${this.index}`);
        const operators = ['==', '!=', '===', '!=='];
        let result = this.parseRelational();
        while (true) {
            let found = false;
            for (const operator of operators) {
                if (this.advancePastOptionalOperator(operator)) {
                    result = new Binary(this.getTokenRequired(-1).position, operator, result, this.parseRelational());
                    found = true;
                    break;
                }
            }
            if (!found)
                return result;
        }
    }
    parseExpression() {
        if (debug)
            console.log(`jsj: parseExpression: index = ${this.index}`);
        let result = this.parseConditional();
        while (true) {
            const equals = this.getToken();
            if (equals === undefined || equals.value !== '=')
                break;
            if (this.options.rejectAssignment) {
                throw new ParseError(equals.position, 'assignment is not allowed');
            }
            if (!result.isAssignable) {
                throw new ParseError(result.position, 'expression is not assignable 1');
            }
            this.advancePastRequiredOperator('=');
            const expression = this.parseConditional();
            result = new Assignment(result.position, result, expression);
        }
        return result;
    }
    parseExpressionList(terminator) {
        if (debug)
            console.log(`jsj: parseExpressionList: index = ${this.index}`);
        const list = [];
        do {
            if (this.getTokenRequired().value === terminator)
                break;
            list.push(this.parseExpression());
        } while (this.advancePastOptionalOperator(','));
        this.advancePastRequiredOperator(terminator);
        return list;
    }
    parseLogicalAnd() {
        if (debug)
            console.log(`jsj: parseLogicalAnd: index = ${this.index}`);
        let result = this.parseEquality();
        while (this.advancePastOptionalOperator('&&')) {
            result = new Binary(this.getTokenRequired(-1).position, '&&', result, this.parseEquality());
        }
        return result;
    }
    parseLogicalOr() {
        if (debug)
            console.log(`jsj: parseLogicalOr: index = ${this.index}`);
        const operators = ['||', '??'];
        let result = this.parseLogicalAnd();
        while (true) {
            let found = false;
            for (const operator of operators) {
                if (this.advancePastOptionalOperator(operator)) {
                    if (debug)
                        console.log(`    found binary operator '${operator}'`);
                    result = new Binary(this.getTokenRequired(-1).position, operator, result, this.parseLogicalAnd());
                    found = true;
                }
            }
            if (!found)
                return result;
        }
    }
    parseMultiplicative() {
        if (debug)
            console.log(`jsj: parseMultiplicative: index = ${this.index}`);
        const operators = ['*', '%', '/'];
        let result = this.parsePrefix();
        while (true) {
            let found = false;
            for (const operator of operators) {
                if (this.advancePastOptionalOperator(operator)) {
                    result = new Binary(this.getTokenRequired(-1).position, operator, result, this.parsePrefix());
                    found = true;
                }
            }
            if (!found)
                return result;
        }
    }
    parseObject() {
        if (debug)
            console.log(`jsj: parseObject: index = ${this.index}`);
        const token = this.getTokenRequired(-1);
        const obj = {};
        do {
            if (this.getTokenRequired().value === '}')
                break;
            // parse key
            const keyToken = this.getTokenRequired();
            this.advance();
            if (!(keyToken instanceof IdentifierToken && typeof keyToken.value === 'string') &&
                !(keyToken instanceof ValueToken && typeof keyToken.value === 'string')) {
                throw new ParseError(keyToken.position, `invalid key: "${keyToken.value}}"`);
            }
            this.advancePastRequiredOperator(':');
            // parse value
            obj[keyToken.value] = this.parseExpression();
        } while (this.advancePastOptionalOperator(','));
        this.advancePastRequiredOperator('}');
        return new LiteralObject(token.position, obj);
    }
    parsePrefix() {
        if (debug)
            console.log(`jsj: parsePrefix: index = ${this.index}`);
        if (this.advancePastOptionalOperator('+')) {
            return this.parsePrefix();
        }
        else if (this.advancePastOptionalOperator('-')) {
            const position = this.getTokenRequired(-1).position;
            return new Binary(position, '-', new LiteralPrimitive(position, 0), this.parsePrefix());
        }
        else if (this.advancePastOptionalOperator('!')) {
            const position = this.getTokenRequired(-1).position;
            return new PrefixNot(position, '!', this.parsePrefix());
        }
        return this.parseAccessOrCallMember();
    }
    parsePrimary() {
        if (debug)
            console.log(`jsj: parsePrimary: index = ${this.index}`);
        if (this.advancePastOptionalOperator('(')) {
            const result = this.parseExpression();
            this.advancePastRequiredOperator(')');
            return result;
        }
        else if (this.advancePastOptionalOperator('`')) {
            const result = this.parseStringInterpolation();
            this.advancePastRequiredOperator('`');
            return result;
        }
        else if (this.advancePastOptionalValue('null')) {
            return new LiteralPrimitive(this.getTokenRequired(-1).position, null);
        }
        else if (this.advancePastOptionalValue('undefined')) {
            return new LiteralPrimitive(this.getTokenRequired(-1).position, undefined);
        }
        else if (this.advancePastOptionalValue('true')) {
            return new LiteralPrimitive(this.getTokenRequired(-1).position, true);
        }
        else if (this.advancePastOptionalValue('false')) {
            return new LiteralPrimitive(this.getTokenRequired(-1).position, false);
        }
        else if (this.advancePastOptionalOperator('[')) {
            const token = this.getTokenRequired(-1);
            const elements = this.parseExpressionList(']');
            return new LiteralArray(token.position, elements);
        }
        else if (this.advancePastOptionalOperator('{')) {
            return this.parseObject();
        }
        else if (this.getTokenRequired() instanceof IdentifierToken) {
            return this.parseAccessOrCallScope();
        }
        else if (this.getTokenRequired() instanceof ValueToken) {
            const token = this.getTokenRequired();
            this.advance();
            return new LiteralPrimitive(token.position, token.value);
        }
        else if (this.index >= this.tokens.length) {
            throw new ParseError(this.getTokenRequired().position, `unexpected end of expression`);
        }
        else {
            const token = this.getTokenRequired();
            throw new ParseError(token.position, `unexpected token: "${token.value}"`);
        }
    }
    parseRelational() {
        if (debug)
            console.log(`jsj: parseRelational: index = ${this.index}`);
        const operators = ['<', '>', '<=', '>='];
        let result = this.parseAdditive();
        while (true) {
            let found = false;
            for (const operator of operators) {
                if (this.advancePastOptionalOperator(operator)) {
                    result = new Binary(this.getTokenRequired(-1).position, operator, result, this.parseAdditive());
                    found = true;
                    break;
                }
            }
            if (!found)
                return result;
        }
    }
    parseStringInterpolation() {
        var _a;
        if (debug)
            console.log(`jsj: parseStringInterpolation: index = ${this.index}`);
        const parts = [];
        let token = this.getToken();
        const start = token;
        while (token !== undefined && !(token instanceof OperatorToken && token.value === '`')) {
            if (this.advancePastOptionalOperator('${')) {
                const part = this.parseExpression();
                this.advancePastRequiredOperator('}');
                parts.push(part);
            }
            else if (typeof token.value === 'string') {
                parts.push(new LiteralPrimitive(token.position, token.value));
                this.advance();
            }
            token = this.getToken();
        }
        return new StringInterpolation((_a = start === null || start === void 0 ? void 0 : start.position) !== null && _a !== void 0 ? _a : 0, parts);
    }
}
